import { useEffect, useState } from "react";
import { Check, X, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Stars } from "@/components/site/ProductCard";
import { toast } from "sonner";

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [status, setStatus] = useState("pending");

  const load = () => api.get("/admin/reviews", { params: status ? { status } : {} })
    .then((r) => setReviews(r.data)).catch(() => {});
  useEffect(load, [status]);

  const act = async (id, action) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { action });
      toast.success(action === "approve" ? "Review published" : "Review rejected");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-reviews">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif-display text-3xl text-white font-light">Review Moderation</h1>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="reviews-status-filter"
          className="bg-[#0D0D0D] border border-[#D4AF37]/20 text-[#B8B8B8] font-sans-body text-xs px-3 py-2.5 focus:outline-none focus:border-[#D4AF37]">
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="">All</option>
        </select>
      </div>

      <div className="space-y-3">
        {reviews.length === 0 && (
          <div className="gold-hairline bg-[#0D0D0D] p-10 text-center font-sans-body text-sm text-[#B8B8B8]" data-testid="reviews-admin-empty">
            No {status || ""} reviews.
          </div>
        )}
        {reviews.map((r) => (
          <div key={r.id} className="gold-hairline bg-[#0D0D0D] p-5 flex flex-wrap items-start gap-4" data-testid={`admin-review-${r.id}`}>
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-3">
                <Stars rating={r.rating} />
                <span className="font-sans-body text-[11px] uppercase tracking-[0.2em] text-[#C6A15B]">{r.display_name}</span>
                <span className="font-sans-body text-[10px] text-[#666666]">on {r.product_name}</span>
              </div>
              <p className="font-sans-body text-sm text-white mt-3 font-light">{r.text}</p>
              <p className="font-sans-body text-[10px] text-[#666666] mt-2">{new Date(r.created_at).toLocaleString()}</p>
            </div>
            <div className="flex gap-2">
              {r.status !== "approved" && (
                <button onClick={() => act(r.id, "approve")} className="p-2 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                  data-testid={`review-approve-${r.id}`} aria-label="Approve">
                  <Check size={15} />
                </button>
              )}
              {r.status !== "rejected" && (
                <button onClick={() => act(r.id, "reject")} className="p-2 border border-[#D4AF37]/30 text-[#C6A15B] hover:bg-[#D4AF37]/10"
                  data-testid={`review-reject-${r.id}`} aria-label="Reject">
                  <X size={15} />
                </button>
              )}
              <button onClick={() => remove(r.id)} className="p-2 border border-red-500/30 text-red-400 hover:bg-red-500/10"
                data-testid={`review-delete-${r.id}`} aria-label="Delete">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
